/**
 * Translates a real `ApiError` (STEP 12 "API Error Handler") into a
 * short, user-facing message and a severity — the one place that
 * mapping lives, so a toast, an inline form banner, and a dialog all
 * describe the same error identically. Deliberately does NOT map
 * individual `ApiError.code` values to bespoke copy — the backend's
 * own error-code-to-wire-mapping gap means every module's error
 * currently surfaces a GENERIC `AppError` code (`GEN_CONFLICT`,
 * `GEN_VALIDATION_ERROR`, ...), never a module-specific one
 * (`CLINICAL-REST-ARCHITECTURE.md` §6, `FRONTEND-BACKEND-INTEGRATION.md`
 * §6) — so this maps by HTTP status/shape, the one thing that IS
 * reliable today, not by a code catalog that doesn't reach the wire
 * yet.
 */
import { ApiError } from '@app-types/api.types';

export interface UserFacingError {
  title: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  /**
   * What the person should DO about it, in plain words. Shown under the message
   * in the centred notification (desk, 2026-09-27: "elaborated, understandable
   * way to user"). Derived from the HTTP status because that is the one signal
   * that reaches the wire reliably (see the header of this file).
   */
  nextStep: string;
}

/**
 * The plain-language "what now" for a failed call, by status.
 *
 * Written for a clerk at a counter, not an engineer: it says whether the
 * entry can be trusted, and who to go to. It never claims something was saved
 * or not saved unless the status proves it — a 5xx or a dropped connection
 * proves neither, so those two say to CHECK before repeating (a repeated
 * payment or bill is worse than a slow one).
 */
export function nextStepFor(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Try the action once more. If the same message comes back, tell your supervisor and mention the time shown below.';
  }
  if (error.statusCode === 0) {
    return 'Check that this computer is connected to the network, then look the record up before you repeat the action — it may or may not have gone through, and repeating it could create a second entry.';
  }
  if (error.isValidationError) return 'Read the message, correct the field it names and submit again. Nothing was saved.';
  if (error.isAuthError) return 'Log in again to continue. Anything you had typed on this screen may need to be entered again.';
  if (error.isForbidden) {
    return 'Your login is not allowed to do this, and nothing was changed. Ask your supervisor or the system administrator to give your role this access.';
  }
  if (error.isNotFound) return 'The record may have been removed or moved. Go back, refresh the list and try again.';
  if (error.isConflict) {
    return 'Someone else may have changed this at the same moment, or it already exists. Refresh the screen and check the latest details before you try again.';
  }
  if (error.statusCode >= 500) {
    return 'This is a problem on the system side, not a mistake in your entry. Wait a moment and look the record up before repeating it. If it keeps failing, tell IT and mention the time shown below.';
  }
  return 'Check what you entered and try again. If the same message comes back, tell your supervisor and mention the time shown below.';
}

/**
 * The most recent failure this file described, kept for a few seconds so the
 * notification that follows it can pick up the matching "what now".
 *
 * WHY A SIDE CHANNEL. ~350 call sites do
 * `notify.error({ title, message: toUserFacingError(e).message })` — they pass
 * on the TEXT only, so the status (and with it the advice) is gone by the time
 * `notify` runs. Editing 350 call sites to thread it through is exactly the
 * churn a single seam exists to avoid. `toUserFacingError` runs immediately
 * before `notify` at every one of them, so it records here and `notify` looks
 * the message up by its exact text.
 */
let recent: { message: string; nextStep: string; outage: boolean; at: number } | null = null;
const RECENT_WINDOW_MS = 10_000;

/**
 * True when the server could not be reached at all, or a gateway in front of it
 * answered instead — the two shapes a deploy or restart produces (a dropped
 * connection, or nginx's 502/503/504 while the workers reload). Deliberately NOT
 * true for a request the user cancelled, nor for any status the application
 * itself returned: those are answers, not an outage.
 */
export function isBackendOutage(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.code === 'CLIENT_CANCELED') return false;
  return error.statusCode === 0 || error.statusCode === 502 || error.statusCode === 503 || error.statusCode === 504;
}

/** True when the message about to be shown was produced by an outage, so it can wait for the verdict instead of appearing as its own error. */
export function recentWasOutage(message: string | undefined): boolean {
  return Boolean(message && recent && Date.now() - recent.at <= RECENT_WINDOW_MS && recent.message === message && recent.outage);
}

/** The advice recorded for a message THIS SECOND, or undefined. Matches on exact text so an unrelated notification can never borrow it. */
export function recentNextStepFor(message: string | undefined): string | undefined {
  if (!message || !recent || Date.now() - recent.at > RECENT_WINDOW_MS) return undefined;
  return recent.message === message ? recent.nextStep : undefined;
}

export function toUserFacingError(error: unknown): UserFacingError {
  const described = describe(error);
  recent = { message: described.message, nextStep: described.nextStep, outage: isBackendOutage(error), at: Date.now() };
  return described;
}

function describe(error: unknown): UserFacingError {
  const nextStep = nextStepFor(error);
  const result = describeStatus(error);
  return { ...result, nextStep };
}

function describeStatus(error: unknown): Omit<UserFacingError, 'nextStep'> {
  if (!(error instanceof ApiError)) {
    return { title: 'Unexpected error', message: 'Something went wrong. Please try again.', severity: 'error' };
  }

  if (error.statusCode === 0) {
    return { title: 'Connection problem', message: 'Unable to reach the server — check your connection and try again.', severity: 'warning' };
  }

  if (error.isValidationError) {
    return { title: 'Check the highlighted fields', message: error.message, severity: 'warning' };
  }

  if (error.isAuthError) {
    return { title: 'Session expired', message: 'Please log in again to continue.', severity: 'warning' };
  }

  if (error.isForbidden) {
    return { title: 'Not permitted', message: "You don't have permission to do that.", severity: 'error' };
  }

  if (error.isNotFound) {
    return { title: 'Not found', message: 'The requested item could not be found — it may have been removed.', severity: 'info' };
  }

  if (error.isConflict) {
    return {
      title: 'Conflict',
      message: error.message || 'This record was changed by someone else — please refresh and try again.',
      severity: 'warning',
    };
  }

  return { title: 'Something went wrong', message: error.message || 'An unexpected error occurred.', severity: 'error' };
}

/** Extracts field-level messages from a 422's `details` (Zod's own `flatten().fieldErrors` shape, `core/validation/validate-request.middleware.ts`) for wiring straight into React Hook Form's `setError`. */
export function extractFieldErrors(error: unknown): Record<string, string> | null {
  if (!(error instanceof ApiError) || !error.isValidationError || !error.details) {
    return null;
  }
  const details = error.details as { fieldErrors?: Record<string, string[]> };
  if (!details.fieldErrors) return null;

  const result: Record<string, string> = {};
  for (const [field, messages] of Object.entries(details.fieldErrors)) {
    if (messages?.[0]) result[field] = messages[0];
  }
  return result;
}
